import {
  ChangeEvent,
  FormEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import ConsentWidget, {
  type ConsentForm,
  GLOBAL_BACKEND_API,
} from '../../components/ConsentWidget';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import AppContext from '../../contexts/AppContext';
import DataContext from '../../contexts/DataContext';

import Button from '../../components/Button';
import Input from '../../components/Input';
import { formatPhoneNumber } from '../../helpers';

import Slide from '../components/Slide';
import { contact } from '../data/pages';
import { generateFunnelAuthJwt } from '../../utils/funnelAuthJwt';

interface FormElements extends HTMLFormControlsCollection {
  fullName: HTMLInputElement;
  phone: HTMLInputElement;
}

interface ContactFormElement extends HTMLFormElement {
  readonly elements: FormElements;
}

export default function Contact() {
  const { urlParams } = useContext(AppContext);
  const { data, updateData } = useContext(DataContext);
  const { i18n } = useTranslation();
  const localeCode = i18n.language || 'en';

  const [nameHaveError, setNameHaveError] = useState(false);
  const [phoneHaveError, setPhoneHaveError] = useState(false);
  const navigate = useNavigate();
  const titleRef: any = useRef(null);
  const consentFormRef = useRef<ConsentForm | null>(null);

  const { fullName, phone } = data;
  const { button, heading, inputData } = contact;
  const isButtonDisabled =
    !(fullName && phone) || nameHaveError || phoneHaveError;

  useEffect(() => {
    setTimeout(
      titleRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'center',
      })
    );
  }, [titleRef]);

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value.trim();
    const nameArray = name.split(' ');

    if (nameArray.length >= 2) {
      updateData({
        ...data,
        fullName: name,
      });
      setNameHaveError(false);
    } else {
      updateData({
        ...data,
        fullName: null,
      });
      setNameHaveError(true);
    }
  };

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    const number = event.target.value.trim();
    const realNumber = number.replace(/-|\(|\)|\s/g, '');

    if (realNumber.length >= 10) {
      if (realNumber.length > 10) {
        return;
      }
      setPhoneHaveError(false);
      const formattedPhoneNumber = formatPhoneNumber(number);
      updateData({
        ...data,
        phone: formattedPhoneNumber,
      });
    } else {
      setPhoneHaveError(true);
      const formattedPhoneNumber = formatPhoneNumber(number);
      updateData({
        ...data,
        phone: formattedPhoneNumber,
      });
    }
  };

  const handleSubmit = async (event: FormEvent<ContactFormElement>) => {
    event.preventDefault();

    dataLayer.push({ event: 'seform_contact_information_entered' });

    if (process.env.REACT_APP_ENV !== 'local') {
      const lastSubmittedTime = localStorage.getItem('slf-last-submitted');
      if (lastSubmittedTime) {
        const now = Date.now();
        const secondsPassed = (now - Number(lastSubmittedTime)) / 1000;
        if (secondsPassed < 60 * 5) return;
      }
      localStorage.setItem('slf-last-submitted', String(Date.now()));
    }

    if (consentFormRef.current) {
      try {
        const consentPayload =
          await consentFormRef.current.getSubmissionPayload();
        updateData({ ...data, consentPayload });
        const token = await generateFunnelAuthJwt();
        if (!token) {
          return;
        }
        const consentUrl = `${GLOBAL_BACKEND_API}/v1/consent/update?locale=${localeCode}`;
        const response = await fetch(consentUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...consentPayload,
            email: data.email,
            phoneNumber: data.phone,
          }),
        });
        if (!response.ok) {
          const errorText = await response.text();
        }
      } catch (e) {}
    }

    // On form submit success, navigate to success page
    navigate(`/calculating${urlParams}`);
  };

  return (
    <>
      <Slide heading={heading} titleRef={titleRef} />
      <form onSubmit={handleSubmit}>
        <p className="mt-[30px] text-[17px] short:!text-[16px] md:text-[18px]">
          {inputData.fullName.label}
        </p>
        <Input
          className="mt-3 md:mt-4 mb-6 md:mb-6 md:w-[400px]"
          errorMessage={inputData.fullName.errorMessage}
          haveError={nameHaveError}
          name={inputData.fullName.name}
          onChange={handleNameChange}
          placeholder={inputData.fullName.placeholder}
          value={fullName}
        />
        <p className="mt-[20px] md:mt-6 text-[17px] short:!text-[16px] md:text-[18px]">
          {inputData.phone.label}
        </p>
        <Input
          className="mt-3 md:mt-4 mb-6 md:mb-10 md:w-[400px]"
          errorMessage={inputData.phone.errorMessage}
          haveError={phoneHaveError}
          name={inputData.phone.name}
          onChange={handlePhoneChange}
          placeholder={inputData.phone.placeholder}
          type={inputData.phone.type}
          value={phone}
        />
        <input type="hidden" id="leadid_tcpa_disclosure" />
        <ConsentWidget
          onReady={(form) => {
            consentFormRef.current = form;
          }}
        />

        <div className="mt-[18px] md:mt-[40px]">
          <Button
            disabled={isButtonDisabled}
            title={button.title}
            type={button.type}
            // data-zuko-id="contact_continue"
          />
        </div>
      </form>
    </>
  );
}
